import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Info, Loader2, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/shared/cn';
import type { ThreadNotice, ThreadNoticeSeverity } from '@/versions/v1/shared/chat-types';
import { useChat } from '@/versions/v1/shared/ChatContext';
import { SEED_THREAD_NOTICES } from '@/versions/v1/shared/thread-seed';

function severityIcon(severity: ThreadNoticeSeverity) {
  if (severity === 'critical') {
    return <TriangleAlert className="h-4 w-4 shrink-0 text-red-500" aria-hidden />;
  }
  if (severity === 'warning') {
    return <TriangleAlert className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />;
  }
  return <Info className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden />;
}

export function ThreadNoticeRail() {
  const { currentThreadId } = useChat();
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const dismissTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const notices = useMemo(() => {
    const seeded = SEED_THREAD_NOTICES[currentThreadId] ?? [];
    return seeded.filter((notice) => !dismissed.has(notice.id));
  }, [currentThreadId, dismissed]);

  if (loading) {
    return (
      <div
        className="relative z-10 mb-2 flex w-full items-center gap-2 text-sm text-muted"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading notices…
      </div>
    );
  }

  if (notices.length === 0) return null;

  return (
    <div className="relative z-10 mb-2 w-full">
      <ul
        role="list"
        aria-label="Thread notices"
        className="flex max-h-40 flex-col gap-1.5 overflow-y-auto scrollbar-subtle"
      >
        {notices.map((notice) => (
          <NoticeRow
            key={notice.id}
            notice={notice}
            onDismiss={() => {
              setLoading(true);
              if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
              dismissTimerRef.current = window.setTimeout(() => {
                dismissTimerRef.current = null;
                setDismissed((prev) => new Set(prev).add(notice.id));
                setLoading(false);
              }, 120);
            }}
          />
        ))}
      </ul>
    </div>
  );
}

function NoticeRow({
  notice,
  onDismiss,
}: {
  notice: ThreadNotice;
  onDismiss: () => void;
}) {
  return (
    <li
      role="listitem"
      className="flex items-start gap-2 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface-strong)] px-3 py-2 text-sm text-zinc-700 shadow-[0_4px_12px_-6px_rgba(20,24,40,0.08)] backdrop-blur-md dark:text-zinc-200"
    >
      {severityIcon(notice.severity)}
      <div className="min-w-0 flex-1">
        <p className="font-light leading-snug">{notice.body}</p>
        {notice.actionHint ? (
          <p className="mt-0.5 text-xs text-muted">{notice.actionHint}</p>
        ) : null}
      </div>
      <button
        type="button"
        aria-label={`Dismiss notice: ${notice.kind}`}
        title="Dismiss"
        onClick={onDismiss}
        className={cn(
          'mt-0.5 shrink-0 rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200',
        )}
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </li>
  );
}

export function CompactConversationNotice({ count }: { count: number }) {
  if (count <= 0) return null;

  return (
    <div
      className="mx-auto w-full max-w-[var(--thread-max-width)] px-4 py-2"
      role="status"
    >
      <div
        className="flex items-center gap-2 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-3 py-2 text-sm text-muted"
      >
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
        <span>Conversation compacted · {count} earlier turns</span>
      </div>
    </div>
  );
}
